/**
 * PopoutPanel — the browser portal's stand-in for an OS pop-out window
 * (Task 4094).
 *
 * On desktop, Customer History and Review Thread each open a real
 * `WebviewWindow`. The browser portal has no such thing, so this renders the
 * *same page component* as a full-viewport in-page overlay inside the live,
 * already-authenticated app.
 *
 * ## Why a panel and not `window.open`
 *
 * This was Ben's call (2026-08-26), diverging from the task doc's literal
 * "open as browser windows". A `window.open` implementation has two problems
 * that a panel simply does not have:
 *
 *   - **Popup blockers.** `window.open` only survives inside a direct user
 *     gesture. Both call sites open from a store update consumed in an effect,
 *     one of them behind an `await` on an interaction lookup. Restructuring
 *     that into a synchronous click handler is possible but fragile, and it
 *     fails silently for the operator while working on a developer machine.
 *   - **Authentication.** A new browser document re-runs `main.tsx`, whose
 *     `?ch=1` / `?rt=1` bootstraps call `getEnvironmentConfigs()` — a Tauri
 *     command that *throws by design* in the browser build (Task 4093). The
 *     popped-out document would load and immediately die. A panel renders
 *     inside the running app and inherits the configured `api` client, so
 *     there is no second sign-in and no bootstrap at all.
 *
 * Freshness follows for free: "re-open the same customer and see current data"
 * is a **remount** here, driven by the caller's `key`, rather than named-target
 * window reuse plus a `setTimeout` teardown race.
 *
 * ## Shape
 *
 * Deliberately mirrors `features/sessions/components/SessionPopoutModal` — the
 * house pattern for a full-viewport overlay. It uses the Radix Dialog
 * primitives directly rather than the shared `<DialogContent>` wrapper, whose
 * fixed 512 px max-width is far too narrow for a reading surface.
 */

import { useCallback, type CSSProperties, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

export interface PopoutPanelProps {
  /** Accessible title. Rendered for screen readers only — the hosted page draws its own header. */
  title: string
  /** Accessible description, likewise screen-reader only. */
  description?: string
  /**
   * Vestigial as currently used (review round 1, N3): both call sites
   * `return null` before rendering this component, so they only ever pass
   * `true` and "closed" is expressed by unmounting. Kept because it keeps the
   * closed state in exactly one place (the store) while leaving the Radix
   * `open` / `onOpenChange` contract intact for any future caller that wants
   * to keep the panel mounted while hidden.
   */
  open: boolean
  onClose: () => void
  children: ReactNode
}

export function PopoutPanel({
  title,
  description,
  open,
  onClose,
  children,
}: PopoutPanelProps): ReactNode {
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) onClose()
    },
    [onClose],
  )

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay style={styles.overlay} data-testid="popout-panel-overlay" />
        <DialogPrimitive.Content
          style={styles.content}
          data-testid="popout-panel"
          aria-label={title}
        >
          {/* Radix requires both, and the hosted page already renders a visible
              header, so these are screen-reader only rather than duplicated. */}
          <DialogPrimitive.Title style={styles.srOnly}>{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description style={styles.srOnly}>
            {description ?? 'Press Escape to close.'}
          </DialogPrimitive.Description>

          <DialogPrimitive.Close
            style={styles.close}
            data-testid="popout-panel-close"
            aria-label="Close"
          >
            <X style={{ width: '16px', height: '16px' }} />
          </DialogPrimitive.Close>

          <div style={styles.body}>{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: 'rgba(0, 0, 0, 0.65)',
    backdropFilter: 'blur(8px)',
  },
  content: {
    position: 'fixed',
    top: '3vh',
    left: '3vw',
    right: '3vw',
    bottom: '3vh',
    zIndex: 201,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04)',
    outline: 'none',
  },
  close: {
    position: 'absolute',
    right: '12px',
    top: '12px',
    zIndex: 1,
    borderRadius: 'var(--radius-sm)',
    opacity: 0.7,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    padding: '6px',
    lineHeight: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}

export default PopoutPanel
